import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CursorTooltip } from '@/ui/primitives';
import {
  tooltipOverlayCompactMaxWidth,
  tooltipOverlayCompactPaddingX,
  tooltipOverlayCompactPaddingY,
} from '@/ui/primitives/tooltipOverlayStyles';
import { elementOrDescendantHasOverflow } from '../table/cells/overflowTooltipUtils';
import { TagChip } from './TagChip';
import { tagChipSx } from './tagChipStyles';
import type { ChipProps, SxProps, Theme } from '@/ui/primitives';
import type { ReactElement } from 'react';

type TagChipWithTooltipProps = {
  label: string;
  tone: ChipProps['tone'];
  sx?: SxProps<Theme>;
  tooltipSx?: SxProps<Theme>;
  wrapperSx?: SxProps<Theme>;
};

export const TagChipWithTooltip = ({
  label,
  tone,
  sx = tagChipSx,
  tooltipSx = tagChipSx,
  wrapperSx,
}: TagChipWithTooltipProps): ReactElement => {
  const chipWrapperRef = useRef<HTMLSpanElement | null>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const hasTruncatedLabel = useCallback((): boolean => {
    const chipWrapper = chipWrapperRef.current;
    if (!chipWrapper) return false;
    const chipLabel = chipWrapper.querySelector<HTMLElement>('.MuiChip-label');
    if (!chipLabel) return false;
    return elementOrDescendantHasOverflow(chipLabel);
  }, []);

  const handleOpen = useCallback(() => {
    setTooltipOpen(hasTruncatedLabel());
  }, [hasTruncatedLabel]);

  const handleClose = useCallback(() => {
    setTooltipOpen(false);
  }, []);

  useEffect(() => {
    if (!tooltipOpen || hasTruncatedLabel()) return;
    setTooltipOpen(false);
  }, [hasTruncatedLabel, tooltipOpen]);

  return (
    <CursorTooltip
      open={tooltipOpen}
      title={<TagChip label={label} tone={tone} sx={tooltipSx} />}
      arrow
      slotProps={{
        tooltip: {
          sx: {
            px: tooltipOverlayCompactPaddingX,
            py: tooltipOverlayCompactPaddingY,
            maxWidth: tooltipOverlayCompactMaxWidth,
            width: 'fit-content',
          },
        },
      }}
    >
      <Box
        ref={chipWrapperRef}
        component="span"
        onBlur={handleClose}
        onFocus={handleOpen}
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        sx={[
          { display: 'inline-flex', maxWidth: '100%', minWidth: 0 },
          ...(wrapperSx ? (Array.isArray(wrapperSx) ? wrapperSx : [wrapperSx]) : []),
        ]}
      >
        <TagChip label={label} tone={tone} sx={sx} />
      </Box>
    </CursorTooltip>
  );
};
